import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { addEntities, setAllEntities, withEntities } from '@ngrx/signals/entities';

import type { Notification } from './notifications.model';
import { NOTIFICATIONS_PORT } from './notifications.tokens';

/**
 * APPLICATION — Store de la bandeja de notificaciones (`signalStore` + entities).
 *
 * `providedIn: 'root'`: singleton del shell. Lo consumen la campana del topbar
 * (layout) y la página `/notifications` (feature) — misma instancia, una sola
 * fuente de verdad. NO carga en el constructor: la campana dispara `init()`/`load()`
 * explícitamente (y sólo si el usuario tiene el permiso, para no gatillar un 403
 * que el interceptor global toastearía).
 *
 * Paginación incremental (fix decisiones r2): `load()` trae la página 1 con
 * `pageSize` explícito y la página completa appendea con `loadMore()` hasta
 * agotar el `total` del envelope — sin truncado silencioso.
 */
interface NotificationsState {
  readonly loading: boolean;
  /** Error de la carga inicial/recarga (`load()`) — rama de error de página completa. */
  readonly error: string | null;
  /**
   * Error de `loadMore()`, SEPARADO del error general: un fallo al pedir la
   * página siguiente no puede borrar lo ya listado ni disparar la rama de
   * error de página completa (cuyo reintento llama `load()` y resetea a página 1).
   */
  readonly loadMoreError: string | null;
  /** Total del envelope paginado del backend (para el badge de la campana). */
  readonly total: number;
  /** Marca de "al menos un intento de carga" (distingue vacío-cargado de nunca-cargado). */
  readonly loaded: boolean;
  /** Página más alta ya cargada (1-based, server-side). */
  readonly page: number;
  /** Tamaño de página solicitado a GET /notifications (explícito, no default del server). */
  readonly pageSize: number;
  /** "Cargar más" en vuelo (no oculta lo ya listado). */
  readonly loadingMore: boolean;
}

const initialState: NotificationsState = {
  loading: false,
  error: null,
  loadMoreError: null,
  total: 0,
  loaded: false,
  page: 1,
  pageSize: 50,
  loadingMore: false,
};

export const NotificationsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withEntities<Notification>(),
  withComputed((store) => ({
    /** Cantidad para el badge de la campana (no hay estado leído/no-leído en el contrato). */
    count: computed(() => store.total()),
    /** ¿Hay algo para mostrar en el panel? */
    isEmpty: computed(() => store.loaded() && store.entities().length === 0),
    /** Quedan páginas por traer: lo cargado aún no cubre el total del envelope. */
    hasMore: computed(() => store.loaded() && store.entities().length < store.total()),
  })),
  withMethods((store, port = inject(NOTIFICATIONS_PORT)) => {
    async function load(): Promise<void> {
      // Recarga desde cero: vuelve a página 1 y REEMPLAZA lo listado.
      patchState(store, { loading: true, error: null, loadMoreError: null, page: 1 });
      try {
        const { items, total } = await port.list({ page: 1, pageSize: store.pageSize() });
        patchState(store, setAllEntities([...items]), {
          loading: false,
          total,
          loaded: true,
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Error al cargar las notificaciones';
        patchState(store, { loading: false, error: message, loaded: true });
      }
    }

    return {
      /** Recarga forzada del listado (p.ej. al abrir el panel). */
      load,

      /** Carga inicial idempotente: no reintenta si ya se cargó una vez. */
      async init(): Promise<void> {
        if (store.loaded() || store.loading()) return;
        await load();
      },

      /**
       * Trae la página siguiente y la APPENDEA a lo listado (paginación
       * incremental de la página completa). No-op si no quedan páginas o si
       * ya hay una carga en vuelo.
       */
      async loadMore(): Promise<void> {
        if (store.loading() || store.loadingMore() || !store.hasMore()) return;
        const next = store.page() + 1;
        patchState(store, { loadingMore: true, loadMoreError: null });
        try {
          const { items, total } = await port.list({ page: next, pageSize: store.pageSize() });
          patchState(store, addEntities([...items]), {
            loadingMore: false,
            page: next,
            total,
          });
        } catch (err) {
          const message =
            err instanceof Error ? err.message : 'Error al cargar las notificaciones';
          // Error PROPIO de loadMore: el `error` general queda intacto para que
          // la página no descarte las notificaciones ya renderizadas.
          patchState(store, { loadingMore: false, loadMoreError: message });
        }
      },
    };
  }),
);
